import { v4 as uuidv4 } from 'uuid';
import { getUnixTime, parseISO } from 'date-fns';
import chatChannelProvider from '../../chats/chatChannelProvider';
import { AppThunkAction } from '../index';
import { Timestamp } from '../../types/datetime';
import { ChatClient, ChatClientType } from '../../types/chatClient';
import { SupportedChatChannel } from '../../chats/supportedChatChannel';
import { ALL_SENDER_IDS_VALUE } from '../../chats/inboxConstants';
import { createChatChannelsAction } from './index';
import { selectChatClientById, selectChatClientBySenderIdAndPhoneNumber } from '../../reducers/chats/clients';
import { selectAllChatSenders, selectSelectedChatSenderId } from '../../reducers/chats/senders';
import { setSelectedChatSenderId } from './senders';
import phoneNumberService from '../../service/phoneNumberService';
import dateTimeService from '../../service/dateTimeService';
import { portalV2Api } from '../../api/rtkServices/portalV2';
import type { ContactWithCampaign } from '../../api/rtkServices/portalV2';

const NEW_CLIENT_PREFIX = 'NEW-';
const generateNewChatClientId = () => `${NEW_CLIENT_PREFIX}${uuidv4()}`;
const isNewChatClientId = (clientId: string) => clientId.startsWith(NEW_CLIENT_PREFIX);

const addOneChatClientAction = createChatChannelsAction<{ client: ChatClient }>('clients/addOne');
export const upsertOneChatClientAction = createChatChannelsAction<{ client: ChatClient }>('clients/upsertOne');
const setSelectedChatClientIdAction = createChatChannelsAction<{
  clientId: string;
  conversationSenderId?: string;
}>('clients/set');
const replaceChatClientAction = createChatChannelsAction<{
  clientIdToReplace: string;
  client: ChatClient;
}>('clients/replace');

export const setSelectedChatClientId = (
  channel: SupportedChatChannel,
  clientId: string,
  conversationSenderId?: string
) => setSelectedChatClientIdAction(channel, { clientId, conversationSenderId });

const chatClientsFetchPendingAction = createChatChannelsAction('clients/fetchPending');
const chatClientsFetchSuccessAction = createChatChannelsAction<ChatClient[]>('clients/fetchSuccess');
const chatClientsFetchFailureAction = createChatChannelsAction<string>('clients/fetchFailure');

const parseLastMsgTime = (lastMsgTime: string | null | undefined): Timestamp | undefined => {
  if (!lastMsgTime) return undefined;
  try {
    const iso = lastMsgTime.replace(' ', 'T');
    return getUnixTime(parseISO(iso)) as Timestamp;
  } catch {
    return undefined;
  }
};

const mapContactWithCampaignToChatClient = (item: ContactWithCampaign, senderId: string): ChatClient => {
  const isNumeric = phoneNumberService.isValidPhoneNumber(item.number);
  const lastActivityTime = parseLastMsgTime(item.lastMsgTime) ?? (dateTimeService.getCurrentTimestamp() as Timestamp);
  return {
    channel: SupportedChatChannel.SMS,
    id: item.number,
    type: isNumeric ? ChatClientType.NUMERIC : ChatClientType.ALPHANUMERIC,
    name: isNumeric && item.name ? item.name : isNumeric ? undefined : item.number,
    number: isNumeric ? phoneNumberService.toPhoneNumberWithPlusSign(item.number) : item.number,
    senderId,
    foundInContacts: item.foundInContacts,
    lastActivityTime,
    ...(item.agentId != null && { agentId: item.agentId }),
  };
};

export const fetchChatClients = (channel: SupportedChatChannel, senderId: string): AppThunkAction => {
  return async (dispatch) => {
    dispatch(chatClientsFetchPendingAction(channel));

    if (channel === SupportedChatChannel.SMS) {
      try {
        const payload = await dispatch(
          portalV2Api.endpoints.getContactsWithCampaign.initiate(senderId)
        ).unwrap();
        const chatClients = payload.map((item: ContactWithCampaign) =>
          mapContactWithCampaignToChatClient(item, senderId)
        );
        dispatch(chatClientsFetchSuccessAction(channel, chatClients));
      } catch {
        dispatch(chatClientsFetchFailureAction(channel, 'Failed to fetch chat clients'));
      }
      return;
    }

    try {
      const chatClients: ChatClient[] = await chatChannelProvider.get(channel)
        .api.fetchClients(senderId);

      dispatch(chatClientsFetchSuccessAction(channel, chatClients));
    } catch {
      dispatch(chatClientsFetchFailureAction(channel, 'Failed to fetch chat clients'));
    }
  };
};

interface RecordChatClientActivityParams {
  readonly channel: SupportedChatChannel;
  readonly clientId: string;
  readonly senderId?: string;
  readonly activityTime: Timestamp;
}

export const recordChatClientActivity = (params: RecordChatClientActivityParams): AppThunkAction => {
  return (dispatch, getState) => {
    const { channel, clientId, senderId, activityTime } = params;

    const chatClient = selectChatClientById(getState(), channel, clientId, senderId);
    if (chatClient) {
      dispatch(upsertOneChatClientAction(channel, {
        client: {
          ...chatClient,
          lastActivityTime: activityTime,
        },
      }));
    }
  };
};

export const selectOrCreateChatClientByContact = (params: {
  readonly channel: SupportedChatChannel;
  readonly contactName: string;
  readonly contactPhoneNumber: string;
  readonly senderId?: string;
  readonly contactId?: string;
  readonly foundInContacts?: boolean;
}): AppThunkAction => {
  return (dispatch, getState) => {
    const { channel, contactName, contactPhoneNumber, foundInContacts = true } = params;
    const normalizedPhoneNumber = phoneNumberService.toPhoneNumberWithPlusSign(contactPhoneNumber);

    const state = getState();
    const explicitlySelectedSenderId = params.senderId || selectSelectedChatSenderId(state, channel);
    const fallbackSenderId = selectAllChatSenders(state, channel)[0]?.id;
    const resolvedSenderId =
      explicitlySelectedSenderId && explicitlySelectedSenderId !== ALL_SENDER_IDS_VALUE
        ? explicitlySelectedSenderId
        : fallbackSenderId || '';

    if (resolvedSenderId && !selectSelectedChatSenderId(state, channel)) {
      dispatch(setSelectedChatSenderId(channel, resolvedSenderId));
    } else if (resolvedSenderId && params.senderId) {
      dispatch(setSelectedChatSenderId(channel, resolvedSenderId));
    }

    const chatClient = resolvedSenderId
      ? selectChatClientBySenderIdAndPhoneNumber(getState(), {
          channel,
          senderId: resolvedSenderId,
          phoneNumber: normalizedPhoneNumber,
        })
      : undefined;

    const resolveClientId = () => {
      if (chatClient?.id) return chatClient.id;
      if (channel === SupportedChatChannel.SMS && phoneNumberService.isValidPhoneNumber(normalizedPhoneNumber)) {
        return normalizedPhoneNumber.replace(/^\+/, '');
      }
      return generateNewChatClientId();
    };

    const clientId = resolveClientId();
    if (!chatClient) {
      dispatch(addOneChatClientAction(channel, {
        client: {
          channel,
          id: clientId,
          type: phoneNumberService.isValidPhoneNumber(normalizedPhoneNumber)
            ? ChatClientType.NUMERIC
            : ChatClientType.ALPHANUMERIC,
          name: contactName,
          number: normalizedPhoneNumber,
          lastActivityTime: dateTimeService.getCurrentTimestamp(),
          senderId: resolvedSenderId,
          foundInContacts,
        },
      }));
    }

    dispatch(setSelectedChatClientIdAction(channel, { clientId, conversationSenderId: resolvedSenderId }));
  };
};

export const handleChatClientCreated = (client: ChatClient): AppThunkAction => {
  return (dispatch, getState) => {
    const { channel, senderId, number } = client;
    const isNumeric = phoneNumberService.isValidPhoneNumber(number || '');
    const normalizedPhoneNumber = isNumeric
      ? phoneNumberService.toPhoneNumberWithPlusSign(number!)
      : String(number || '');

    const existingChatClient = selectChatClientBySenderIdAndPhoneNumber(getState(), {
      channel,
      senderId,
      phoneNumber: normalizedPhoneNumber,
    });

    if (existingChatClient && isNewChatClientId(existingChatClient.id)) {
      dispatch(replaceChatClientAction(channel, {
        clientIdToReplace: existingChatClient.id,
        client: {
          ...client,
          name: existingChatClient.name,
        },
      }));
    } else {
      dispatch(upsertOneChatClientAction(client.channel, { client }));
    }
  };
};
